import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useGetMeQuery } from 'store/services/user/meApi';
import { toast } from 'react-toastify';
import InfiniteScroll from 'components/InfiniteScroll';
import TextInputField from 'components/TextInputField';
import AddCardsSearchListItem from './components/AddCardsSearchListItem';
import { HoverPreview } from './components/AddCardsSearchListItem/AddCardsSearchListItem';
import CardImageTooltip from 'components/CardLink/CardImageTooltip';
import { MTGCard } from 'types/interfaces/mtgCard';
import {
  useCardsSearchInfiniteQuery,
  useSearchConsolidatedInfiniteQuery,
  ConsolidatedCard
} from 'store/services/cards/search/cardsSearchApi';
import debounce from 'lodash.debounce';
import ImportCardsModal from 'components/ImportCardsModal/ImportCardsModal';
import { AddSetModal } from 'components/AddSetModal';
import { BulkManageAlertsModal } from 'components/BulkManageAlertsModal';
import { SortOrder } from 'types/interfaces/sort';
import './AddCards.css';
import { copyToClipboard } from 'utils/copyToClipboard';

interface Props {
  placeholder: string;
  title?: string;
  subtitle?: string;
  onCardAdd: (identifier: number | string, cardData?: any, searchMode?: 'any' | 'specific') => void;
  useOracleId?: boolean; // Flag to determine whether to use oracleId or cardId
  isHaveList?: boolean; // Flag to enforce specific set selection
  isWantList?: boolean; // Flag to identify want list for share functionality
  onDeleteAll?: () => void; // Callback for delete all functionality
  viewMode?: 'list' | 'comfortable' | 'compact';
  onViewModeChange?: (mode: 'list' | 'comfortable' | 'compact') => void;
  // Search props
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  isSearchOpen?: boolean;
  onSearchToggle?: () => void;
  filteredCount?: number;
  // Sort props
  orderBy?: string;
  order?: SortOrder;
  onSortChange?: (sortKey: string, direction?: SortOrder) => void;
  // Trade filter props (have list only)
  tradeFilter?: 'all' | 'forTrade' | 'notForTrade';
  onTradeFilterChange?: (filter: 'all' | 'forTrade' | 'notForTrade') => void;
  onExportCsv?: () => void;
  isExporting?: boolean;
}
export const AddCards = ({ placeholder, title = "Add cards", subtitle, onCardAdd, useOracleId = false, isHaveList = false, isWantList = false, onDeleteAll, viewMode = 'list', onViewModeChange, searchTerm, onSearchChange, isSearchOpen, onSearchToggle, filteredCount, orderBy, order, onSortChange, tradeFilter, onTradeFilterChange, onExportCsv, isExporting }: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const filterInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Card-art preview shown to the right of the printings list on row hover.
  const [hoveredPreview, setHoveredPreview] = useState<HoverPreview | null>(null);
  const [previewPos, setPreviewPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterText, setFilterText] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  // Force "specific" mode for Have List, otherwise default to "any"
  const [searchMode, setSearchMode] = useState<'any' | 'specific'>(isHaveList ? 'specific' : 'any');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddSetModal, setShowAddSetModal] = useState(false);
  const [showBulkAlertsModal, setShowBulkAlertsModal] = useState(false);
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Honor `?openBulkManage=true` so the SMS Alerts launch announcement can
  // chain into bulk-manage after navigating to Have List. We strip the param
  // immediately so a refresh doesn't re-open the modal.
  useEffect(() => {
    if (!isHaveList) return;
    if (searchParams.get('openBulkManage') !== 'true') return;
    setShowBulkAlertsModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('openBulkManage');
    setSearchParams(next, { replace: true });
  }, [isHaveList, searchParams, setSearchParams]);
  const [isModeSelectorOpen, setIsModeSelectorOpen] = useState(false);
  const [isViewSelectorOpen, setIsViewSelectorOpen] = useState(false);
  const [isSortSelectorOpen, setIsSortSelectorOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const modeSelectorRef = useRef<HTMLDivElement>(null);
  const viewSelectorRef = useRef<HTMLDivElement>(null);
  const sortSelectorRef = useRef<HTMLDivElement>(null);
  const tradeFilterRef = useRef<HTMLDivElement>(null);
  const [isTradeFilterOpen, setIsTradeFilterOpen] = useState(false);

  // Collection search state (for filtering existing cards)
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  // Get current user's username and custom slug for share functionality
  const username = useSelector((state: RootState) => state.auth.user?.username);
  const { data: meData } = useGetMeQuery();
  
  // Regular search for "Specific Set" mode
  const {
    data: specificData,
    hasNextPage: specificHasNextPage,
    isFetching: specificIsFetching,
    isFetchingNextPage: specificIsFetchingNextPage,
    fetchNextPage: specificFetchNextPage
  } = useCardsSearchInfiniteQuery(
    { name: debouncedSearch, filter: debouncedFilter || undefined },
    { skip: !debouncedSearch || searchMode !== 'specific' }
  );
  const specificTotalCount = specificData?.pages?.[0]?.totalCount ?? 0;
  // Keep the filter row mounted while the user is actively filtering, even if
  // their typing narrows totalCount to <=20. We check `filterText` (the immediate
  // input value) NOT `debouncedFilter` (the debounced value sent to the backend):
  // during the 300ms debounce gap `debouncedFilter` is still stale, so using it
  // here would let the row unmount between a keystroke and the debounce firing —
  // dropping keyboard focus mid-typing. Do not "simplify" this to debouncedFilter.
  // The reset paths (clear/Escape, primary search change, card select, mode
  // switch, dropdown close) all clear `filterText`, which lets the row unmount
  // again once the user is done.
  const shouldShowFilterRow =
    searchMode === 'specific' && (specificTotalCount > 20 || filterText.length > 0);
  
  // Consolidated search for "Any Set" mode
  const { 
    data: consolidatedData, 
    hasNextPage: consolidatedHasNextPage, 
    isFetching: consolidatedIsFetching, 
    isFetchingNextPage: consolidatedIsFetchingNextPage, 
    fetchNextPage: consolidatedFetchNextPage 
  } = useSearchConsolidatedInfiniteQuery(
    { name: debouncedSearch },
    { skip: !debouncedSearch || searchMode !== 'any' }
  );
  
  // Use the appropriate data based on search mode
  const isFetching = searchMode === 'any' ? consolidatedIsFetching : specificIsFetching;
  const isFetchingNextPage = searchMode === 'any' ? consolidatedIsFetchingNextPage : specificIsFetchingNextPage;
  const hasNextPage = searchMode === 'any' ? consolidatedHasNextPage : specificHasNextPage;
  const fetchNextPage = searchMode === 'any' ? consolidatedFetchNextPage : specificFetchNextPage;
  
  const isInitialLoading = isFetching && !isFetchingNextPage;
  const allCards: (ConsolidatedCard | MTGCard)[] | undefined = useMemo(() => {
    if (isInitialLoading) return undefined;
    if (searchMode === 'any') {
      return consolidatedData?.pages.flatMap((page) => page.cards) || [];
    } else {
      const cards = specificData?.pages.flatMap((page) => page.cards) || [];
      // Sort alphabetically by set name (A-Z)
      return [...cards].sort((a, b) => (a.setName || '').localeCompare(b.setName || ''));
    }
  }, [isInitialLoading, searchMode, consolidatedData, specificData]);

  // Debounced handler (memoized once)
  const debouncedUpdateSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
        scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdateSearch.cancel();
    };
  }, [debouncedUpdateSearch]);

  // Debounced printing filter handler (only used when searchMode === 'specific'
  // and totalCount > 20). Sends a `filter` query param to /cards/search-light.
  const debouncedUpdateFilter = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedFilter(value);
        scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdateFilter.cancel();
    };
  }, [debouncedUpdateFilter]);

  // Debounced collection search handler
  const debouncedCollectionSearch = useMemo(
    () =>
      debounce((value: string) => {
        onSearchChange?.(value);
      }, 300),
    [onSearchChange]
  );

  useEffect(() => {
    return () => {
      debouncedCollectionSearch.cancel();
    };
  }, [debouncedCollectionSearch]);

  // Sync local search term with parent when search opens
  useEffect(() => {
    if (isSearchOpen && searchTerm) {
      setLocalSearchTerm(searchTerm);
    }
  }, [isSearchOpen, searchTerm]);

  // Handle collection search input change
  const handleCollectionSearchChange = useCallback(
    (text: string) => {
      setLocalSearchTerm(text);
      debouncedCollectionSearch(text);
    },
    [debouncedCollectionSearch]
  );

  // Handle clearing collection search
  const handleClearSearch = useCallback(() => {
    setLocalSearchTerm('');
    onSearchChange?.('');
    onSearchToggle?.();
  }, [onSearchChange, onSearchToggle]);

  // Handle ESC key for collection search
  const handleCollectionSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        handleClearSearch();
      }
    },
    [handleClearSearch]
  );

  // Handle clicks outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowImportMenu(false);
      }
    };

    if (showImportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showImportMenu]);

  // Handle clicks outside mode selector to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modeSelectorRef.current && !modeSelectorRef.current.contains(event.target as Node)) {
        setIsModeSelectorOpen(false);
      }
    };

    if (isModeSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModeSelectorOpen]);

  // Handle clicks outside view selector to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (viewSelectorRef.current && !viewSelectorRef.current.contains(event.target as Node)) {
        setIsViewSelectorOpen(false);
      }
    };

    if (isViewSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isViewSelectorOpen]);

  // Handle clicks outside sort selector to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortSelectorRef.current && !sortSelectorRef.current.contains(event.target as Node)) {
        setIsSortSelectorOpen(false);
      }
    };

    if (isSortSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSortSelectorOpen]);

  // Handle clicks outside trade filter to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tradeFilterRef.current && !tradeFilterRef.current.contains(event.target as Node)) {
        setIsTradeFilterOpen(false);
      }
    };

    if (isTradeFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTradeFilterOpen]);

  // Mode options for custom dropdown
  const MODE_OPTIONS = [
    { value: 'any' as const, label: 'Any Set', iconUrl: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/infinity.svg' },
    { value: 'specific' as const, label: 'Specific Set', iconUrl: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/one.svg' },
  ];

  // View options for custom dropdown
  const VIEW_OPTIONS = [
    { value: 'list' as const, label: 'List View' },
    { value: 'comfortable' as const, label: 'Image View' },
    { value: 'compact' as const, label: 'Images - Compact' },
  ];

  // Sort options for custom dropdown
  // Each option encodes sort key and direction as "key-direction"
  const SORT_OPTIONS = [
    { value: 'created_at-desc', label: 'Date Added' },
    { value: 'updated_at-desc', label: 'Date Updated' },
    { value: 'reference_price-asc', label: 'Price (Low to High)' },
    { value: 'reference_price-desc', label: 'Price (High to Low)' },
    { value: 'card_name-asc', label: 'Alphabetical' },
    // Bucket sorts (labels fixed by the filter-rail spec, decision 7).
    { value: 'color_identity-asc', label: 'Color' },
    { value: 'set_released-asc', label: 'Set (Oldest First)' },
    { value: 'set_released-desc', label: 'Set (Newest First)' },
  ];

  // Trade filter options
  const TRADE_FILTER_OPTIONS = [
    { value: 'all' as const, label: 'All Cards' },
    { value: 'forTrade' as const, label: 'For Trade' },
    { value: 'notForTrade' as const, label: 'Not for Trade' },
  ];

  const currentTradeFilterLabel = TRADE_FILTER_OPTIONS.find(o => o.value === (tradeFilter || 'all'))?.label || 'All Cards';

  // Get the current sort compound value for display
  const currentSortValue = `${orderBy || 'created_at'}-${order || 'desc'}`;
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === currentSortValue)?.label || 'Date Added';

  // Handle sort option selection
  const handleSortSelect = useCallback((compoundValue: string) => {
    const [sortKey, direction] = compoundValue.split('-');
    if (onSortChange) {
      onSortChange(sortKey, direction as SortOrder);
    }
    setIsSortSelectorOpen(false);
  }, [onSortChange]);

  const handleSearchChange = useCallback(
    (text: string) => {
      setSearch(text);
      debouncedUpdateSearch(text);
      setSelectedIndex(-1); // Reset selection when search changes
      setFilterText('');
      setDebouncedFilter('');
      debouncedUpdateFilter.cancel();
    },
    [debouncedUpdateSearch, debouncedUpdateFilter]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!allCards || allCards.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => {
            // Start from first item if nothing selected, otherwise move down
            const newIndex = prev === -1 ? 0 : (prev < allCards.length - 1 ? prev + 1 : prev);
            // Scroll the selected item into view
            if (scrollRef.current && newIndex >= 0) {
              const items = scrollRef.current.querySelectorAll('.add-cards-search-list-item');
              items[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
            return newIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : -1;
            // Scroll the selected item into view
            if (scrollRef.current && newIndex >= 0) {
              const items = scrollRef.current.querySelectorAll('.add-cards-search-list-item');
              items[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < allCards.length) {
            const selectedCard = allCards[selectedIndex];
            if (searchMode === 'any') {
              // For consolidated results, use oracleId
              const consolidatedCard = selectedCard as ConsolidatedCard;
              onCardAdd(consolidatedCard.oracleId, consolidatedCard, searchMode);
            } else {
              // For specific results, use existing logic
              const mtgCard = selectedCard as MTGCard;
              const identifier = useOracleId && mtgCard.oracleId ? mtgCard.oracleId : mtgCard.id;
              onCardAdd(identifier, mtgCard, searchMode);
            }
            setSearch('');
            setDebouncedSearch('');
            setFilterText('');
            setDebouncedFilter('');
            debouncedUpdateFilter.cancel();
            setSelectedIndex(-1);
            // Keep the cursor in the primary search input so the user can
            // immediately type the next card without re-clicking. Clearing
            // `search` unmounts the dropdown overlay (and the filter-row input,
            // which shares this keydown handler), so focus would otherwise fall
            // back to <body>. The input element itself is always mounted, so a
            // synchronous focus() is safe here.
            searchInputRef.current?.focus();
          }
          break;
        case 'Escape':
          e.preventDefault();
          if (filterText) {
            setFilterText('');
            setDebouncedFilter('');
            debouncedUpdateFilter.cancel();
            setSelectedIndex(-1);
          } else {
            setSearch('');
            setDebouncedSearch('');
            setSelectedIndex(-1);
          }
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allCards, onCardAdd, useOracleId, selectedIndex, searchMode, filterText, debouncedUpdateFilter]
  );

  // Show the hovered printing's art in a fixed spot to the right of the list.
  // Anchored to the overlay rect (stable across rows) so the preview stays put
  // and only its art swaps as the cursor moves between rows. A short hover-intent
  // delay avoids flicker when the cursor passes over rows en route to a click.
  const handleRowHover = useCallback((preview: HoverPreview) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      const overlay = overlayRef.current;
      if (!overlay) return;
      const rect = overlay.getBoundingClientRect();
      // Suppress when there's no room for a right-side preview (narrow/mobile)
      // so it never overlaps the clickable rows.
      if (window.innerWidth - rect.right < 256) {
        setHoveredPreview(null);
        return;
      }
      setPreviewPos({ x: rect.right, y: rect.top });
      setHoveredPreview(preview);
    }, 120);
  }, []);

  const handleRowHoverEnd = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setHoveredPreview(null);
  }, []);

  // Clear any pending timer on unmount.
  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  // Drop a stale preview when the results dropdown closes.
  useEffect(() => {
    if (!(search && debouncedSearch)) handleRowHoverEnd();
  }, [search, debouncedSearch, handleRowHoverEnd]);

  const renderItem = (item: ConsolidatedCard | MTGCard, index: number) => {
    const isSelected = index === selectedIndex;
    const additionalStyle = index % 2 === 0 ? '' : 'black';
    const onPress = () => {
      if (searchMode === 'any') {
        // Handle consolidated card
        const consolidatedCard = item as ConsolidatedCard;
        console.log('AddCards - Consolidated card selected:', {
          cardName: consolidatedCard.cardName,
          oracleId: consolidatedCard.oracleId,
          totalVersions: consolidatedCard.totalVersions
        });
        onCardAdd(consolidatedCard.oracleId, consolidatedCard, searchMode);
      } else {
        // Handle specific MTG card
        const mtgCard = item as MTGCard;
        console.log('AddCards - Card selected:', {
          id: mtgCard.id,
          name: mtgCard.name,
          setName: mtgCard.setName,
          setId: mtgCard.setId,
          oracleId: mtgCard.oracleId,
          useOracleId: useOracleId
        });
        
        const identifier = useOracleId && mtgCard.oracleId ? mtgCard.oracleId : mtgCard.id;
        console.log('AddCards - Using identifier:', identifier, 'type:', typeof identifier);
        console.log('AddCards - Passing set:', mtgCard.setName);
        
        onCardAdd(identifier, mtgCard, searchMode);
      }
      setSearch('');
      setDebouncedSearch('');
      setFilterText('');
      setDebouncedFilter('');
      debouncedUpdateFilter.cancel();
      setSelectedIndex(-1);
      handleRowHoverEnd();
    };

    return (
      <AddCardsSearchListItem
        key={searchMode === 'any'
          ? `add-cards-search-item-oracle-${(item as ConsolidatedCard).oracleId}`
          : `add-cards-search-item-id-${(item as MTGCard).id}`}
        item={item}
        additionalStyle={`${additionalStyle} ${isSelected ? 'selected' : ''}`}
        onPress={onPress}
        mode={searchMode}
        onHover={handleRowHover}
        onHoverEnd={handleRowHoverEnd}
      />
    );
  };

  const onListEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Handle share functionality
  const handleShare = async () => {
    if (!username) {
      toast.error('Unable to share: User not found');
      return;
    }

    const tab = isHaveList ? 'have-list' : (isWantList ? 'want-list' : 'have-list');
    const basePath = meData?.customSlug
      ? `https://trademagic.gg/u/${meData.customSlug}`
      : `https://trademagic.gg/profile/u/${username}`;
    const shareUrl = `${basePath}?tab=${tab}`;

    try {
      if (!(await copyToClipboard(shareUrl))) {
        toast.error('Failed to copy link');
        return;
      }
      toast.success('Link copied to clipboard!');
      setShowImportMenu(false);
    } catch (err) {
      console.error('Failed to copy link:', err);
      toast.error('Failed to copy link');
    }
  };

  // Safe subtitle rendering component
  const renderSubtitle = () => {
    if (!subtitle) return null;

    // Check if subtitle contains "send page" text to replace with link
    if (subtitle.includes('send page')) {
      // Split the text around "send page"
      const parts = subtitle.split('send page');

      return (
        <div className="add-cards-subtitle">
          {parts[0]}
          <Link to="/sendcards">send page</Link>
          {parts[1]}
        </div>
      );
    }

    // For other subtitles, render as plain text
    return <div className="add-cards-subtitle">{subtitle}</div>;
  };

  return (
    <>
      <div className="add-cards-header">
        <div className="add-cards-title">
          <span>{title}</span>
        </div>
        {renderSubtitle()}
      </div>
      <div className="add-cards-controls">
        <div className={`add-cards-search-input ${search && debouncedSearch ? 'with-dropdown' : ''}`}>
          <TextInputField
            leftComponent={
              <FontAwesomeIcon
                size="sm"
                icon={'plus'}
                color="#DD5BEA"
              />
            }
            value={search}
            placeholder={placeholder}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            inputRef={searchInputRef}
            autoFocus
          />
          {search && debouncedSearch && (
            <div className="add-cards-list-overlay" ref={overlayRef}>
              {shouldShowFilterRow && (
                <div className="add-cards-filter-row">
                  <span
                    className="add-cards-filter-row__badge"
                    aria-live="polite"
                  >
                    {specificTotalCount} printings
                  </span>
                  <input
                    ref={filterInputRef}
                    className="add-cards-filter-row__input"
                    type="text"
                    value={filterText}
                    placeholder="Filter by set name or collector number"
                    aria-label="Filter results by set or collector number"
                    onChange={(e) => {
                      setFilterText(e.target.value);
                      debouncedUpdateFilter(e.target.value);
                      setSelectedIndex(-1);
                    }}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              )}
              <InfiniteScroll
                ref={scrollRef}
                data={allCards}
                renderItem={renderItem}
                onEndReached={onListEndReached}
                isLoading={isInitialLoading}
                isLoadingMore={isFetchingNextPage}
                emptyListMessage="No cards found"
              />
              {hoveredPreview && (
                <CardImageTooltip
                  scryfallId={hoveredPreview.scryfallId}
                  imageUrl={hoveredPreview.imageUrl}
                  normalFallbacks={hoveredPreview.normalFallbacks}
                  cardName={hoveredPreview.cardName}
                  position={previewPos}
                  onMouseEnter={() => {}}
                  onMouseLeave={() => setHoveredPreview(null)}
                />
              )}
            </div>
          )}
        </div>
        <div
          className={`add-cards-mode-selector-custom ${isHaveList ? 'disabled' : ''}`}
          ref={modeSelectorRef}
        >
          <span className="add-cards-dropdown-label">Printing</span>
          <button
            type="button"
            className={`add-cards-mode-trigger ${isModeSelectorOpen ? 'open' : ''}`}
            onClick={() => !isHaveList && setIsModeSelectorOpen(!isModeSelectorOpen)}
            disabled={isHaveList}
            aria-haspopup="listbox"
            aria-expanded={isModeSelectorOpen}
            aria-label="Select search mode"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsModeSelectorOpen(false);
              }
            }}
          >
            <span
              className="add-cards-mode-icon"
              style={{
                WebkitMask: `url(${MODE_OPTIONS.find(o => o.value === searchMode)?.iconUrl}) no-repeat center`,
                mask: `url(${MODE_OPTIONS.find(o => o.value === searchMode)?.iconUrl}) no-repeat center`
              }}
            />
            <span className="add-cards-mode-label">
              {MODE_OPTIONS.find(o => o.value === searchMode)?.label}
            </span>
            <svg
              className={`add-cards-mode-chevron ${isModeSelectorOpen ? 'open' : ''}`}
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {isModeSelectorOpen && (
            <div className="add-cards-mode-dropdown" role="listbox">
              {MODE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`add-cards-mode-option ${searchMode === option.value ? 'selected' : ''}`}
                  onClick={() => {
                    setSearchMode(option.value);
                    setSelectedIndex(-1);
                    setFilterText('');
                    setDebouncedFilter('');
                    debouncedUpdateFilter.cancel();
                    setIsModeSelectorOpen(false);
                  }}
                  role="option"
                  aria-selected={searchMode === option.value}
                >
                  <span
                    className="add-cards-mode-icon"
                    style={{
                      WebkitMask: `url(${option.iconUrl}) no-repeat center`,
                      mask: `url(${option.iconUrl}) no-repeat center`
                    }}
                  />
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div
          className="add-cards-view-selector-custom"
          ref={viewSelectorRef}
        >
          <span className="add-cards-dropdown-label">View</span>
          <button
            type="button"
            className={`add-cards-view-trigger ${isViewSelectorOpen ? 'open' : ''}`}
            onClick={() => setIsViewSelectorOpen(!isViewSelectorOpen)}
            aria-haspopup="listbox"
            aria-expanded={isViewSelectorOpen}
            aria-label="Select view mode"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsViewSelectorOpen(false);
              }
            }}
          >
            <span className="add-cards-view-label">
              {VIEW_OPTIONS.find(o => o.value === viewMode)?.label}
            </span>
            <svg
              className={`add-cards-view-chevron ${isViewSelectorOpen ? 'open' : ''}`}
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {isViewSelectorOpen && (
            <div className="add-cards-view-dropdown" role="listbox">
              {VIEW_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`add-cards-view-option ${viewMode === option.value ? 'selected' : ''}`}
                  onClick={() => {
                    onViewModeChange?.(option.value);
                    setIsViewSelectorOpen(false);
                  }}
                  role="option"
                  aria-selected={viewMode === option.value}
                >
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {onSortChange && (
          <div
            className="add-cards-sort-selector-custom"
            ref={sortSelectorRef}
          >
            <span className="add-cards-dropdown-label">Sort</span>
            <button
              type="button"
              className={`add-cards-sort-trigger ${isSortSelectorOpen ? 'open' : ''}`}
              onClick={() => setIsSortSelectorOpen(!isSortSelectorOpen)}
              aria-haspopup="listbox"
              aria-expanded={isSortSelectorOpen}
              aria-label="Select sort order"
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsSortSelectorOpen(false);
                }
              }}
            >
              <span className="add-cards-sort-label">
                {currentSortLabel}
              </span>
              <svg
                className={`add-cards-sort-chevron ${isSortSelectorOpen ? 'open' : ''}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {isSortSelectorOpen && (
              <div className="add-cards-sort-dropdown" role="listbox">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`add-cards-sort-option ${currentSortValue === option.value ? 'selected' : ''}`}
                    onClick={() => handleSortSelect(option.value)}
                    role="option"
                    aria-selected={currentSortValue === option.value}
                  >
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {isHaveList && onTradeFilterChange && (
          <div className="add-cards-trade-filter-custom" ref={tradeFilterRef}>
            <span className="add-cards-dropdown-label">Trade</span>
            <button
              type="button"
              className="add-cards-trade-filter-trigger"
              onClick={() => setIsTradeFilterOpen(!isTradeFilterOpen)}
              aria-haspopup="listbox"
              aria-expanded={isTradeFilterOpen}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsTradeFilterOpen(false);
                }
              }}
            >
              <span className="add-cards-trade-filter-label">
                {currentTradeFilterLabel}
              </span>
              <svg
                className={`add-cards-sort-chevron ${isTradeFilterOpen ? 'open' : ''}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {isTradeFilterOpen && (
              <div className="add-cards-trade-filter-dropdown" role="listbox">
                {TRADE_FILTER_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`add-cards-trade-filter-option ${(tradeFilter || 'all') === option.value ? 'selected' : ''}`}
                    onClick={() => {
                      onTradeFilterChange(option.value);
                      setIsTradeFilterOpen(false);
                    }}
                    role="option"
                    aria-selected={(tradeFilter || 'all') === option.value}
                  >
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {onSearchToggle && (
          <button
            className={`add-cards-search-button ${isSearchOpen ? 'active' : ''}`}
            onClick={onSearchToggle}
            aria-label="Search your collection"
            aria-expanded={isSearchOpen}
            title="Search your collection"
          >
            <FontAwesomeIcon icon="magnifying-glass" size="sm" />
          </button>
        )}
        <div className="add-cards-import-menu" ref={menuRef}>
          <button
            className="add-cards-import-btn"
            onClick={() => setShowImportMenu(!showImportMenu)}
            aria-label="More options"
          >
            <img
              src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/more.svg"
              alt="More options"
              className="add-cards-import-icon"
            />
          </button>
          {showImportMenu && (
            <div className="add-cards-import-dropdown">
              <button
                className="add-cards-import-option"
                onClick={() => {
                  setShowImportModal(true);
                  setShowImportMenu(false);
                }}
              >
                <FontAwesomeIcon icon="file-import" style={{ marginRight: '8px' }} />
                Import
              </button>
              {onExportCsv && (
                <button
                  className="add-cards-import-option"
                  onClick={() => {
                    onExportCsv();
                    setShowImportMenu(false);
                  }}
                  disabled={isExporting}
                >
                  <FontAwesomeIcon icon="file-export" style={{ marginRight: '8px' }} />
                  {isExporting ? 'Exporting...' : 'Export'}
                </button>
              )}
              {(isWantList || isHaveList) && (
                <button
                  className="add-cards-import-option"
                  onClick={() => {
                    setShowAddSetModal(true);
                    setShowImportMenu(false);
                  }}
                >
                  <FontAwesomeIcon icon="circle-plus" style={{ marginRight: '8px' }} />
                  Add Set
                </button>
              )}
              <button
                className="add-cards-import-option"
                onClick={handleShare}
              >
                <FontAwesomeIcon icon="share" style={{ marginRight: '8px' }} />
                Share
              </button>

              {isHaveList && (
                <button
                  className="add-cards-import-option"
                  onClick={() => {
                    setShowBulkAlertsModal(true);
                    setShowImportMenu(false);
                  }}
                >
                  <FontAwesomeIcon icon="bell" style={{ marginRight: '8px' }} />
                  Alerts
                </button>
              )}

              {onDeleteAll && (
                <button
                  className="add-cards-delete-option"
                  onClick={() => {
                    onDeleteAll();
                    setShowImportMenu(false);
                  }}
                >
                  <FontAwesomeIcon icon="trash" style={{ marginRight: '8px' }} />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {isSearchOpen && onSearchChange && (
        <div className="add-cards-search-container">
          <TextInputField
            value={localSearchTerm}
            onChange={handleCollectionSearchChange}
            placeholder="Search your collection..."
            autoFocus
            leftComponent={
              <FontAwesomeIcon icon="magnifying-glass" color="#DD5BEA" size="sm" />
            }
            onKeyDown={handleCollectionSearchKeyDown}
          />
          <button
            className="add-cards-search-close"
            onClick={handleClearSearch}
            aria-label="Close search"
            title="Close search"
          >
            <FontAwesomeIcon icon="xmark" size="sm" />
          </button>
          {filteredCount !== undefined && (
            <div className="add-cards-search-count">
              {filteredCount} {filteredCount === 1 ? 'card' : 'cards'} found
            </div>
          )}
        </div>
      )}
      {showImportModal && (
        <ImportCardsModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onCardAdd={onCardAdd}
          useOracleId={useOracleId}
          isHaveList={isHaveList}
        />
      )}
      {(isWantList || isHaveList) && (
        <AddSetModal
          isOpen={showAddSetModal}
          onClose={() => setShowAddSetModal(false)}
          listType={isWantList ? 'want' : 'have'}
        />
      )}
      {isHaveList && (
        <BulkManageAlertsModal
          isOpen={showBulkAlertsModal}
          onClose={() => setShowBulkAlertsModal(false)}
        />
      )}
    </>
  );
};
